import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  MatTab,
  MatTabGroup,
  MatTabGroupBaseHeader,
} from '@angular/material/tabs';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { isDefined, mapToObject } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { getTotalFusedOutputs } from '../../../../cross-team-shared/misc-utils/project-utils';
import { EventTrackingService } from '../../../../shared/event-tracking/event-tracking.service';
import { getOutputHelper } from '../../../../shared/output-helpers/get-output-helper';
import { Output } from '../../../../team-apis/otterdam/outputs/output';
import { setSharedFusionCssProperties } from '../../../outputs/annotation/annotation-shared/fusion/fusion-css-values';
import { FeatureFlagService } from '../../../shared/misc-services/feature-flag.service';
import { ViewContainerMappingService } from '../../../shared/view-container-mapping/view-container-mapping.service';
import { TaskWorkspaceStore } from '../../state/task-workspace-store';
import {
  getFusedOutputs,
  getFusedOutputsObjectless,
  getOutputByName,
} from './sensor-fusion-output.utils';
import { SplitViewInitializationService } from './split-view/split-view-initialization.service';

@Component({
  selector: 'h3-sensor-fusion',
  templateUrl: './sensor-fusion.component.html',
  styleUrls: ['./sensor-fusion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ViewContainerMappingService],
  standalone: false,
})
export class SensorFusionComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @ViewChild('tabGroup') tabGroup!: MatTabGroup;
  @Input() outputs: string[] = [];
  // eslint-disable-next-line @angular-eslint/no-input-rename -- easier for users
  @Input('fused-outputs') fusedOutputs = '';

  // eslint-disable-next-line @angular-eslint/no-input-rename -- easier for users
  @Input('fusion-output') fusionOutput = '';
  // eslint-disable-next-line @angular-eslint/no-input-rename -- easier for users
  @Input('display-order') displayOrder = '';
  validOutputs!: Output[];
  selectedIndex$!: Observable<number>;
  hasFeedback: { [key: string]: Observable<boolean> } = {};
  // public so we can manually trigger it in tests
  observer!: MutationObserver;
  faCircle = faCircle;
  observerCallback: (mutationList: MutationRecord[]) => void;
  taskWorkspaceStore$ = this.taskWorkspaceStore.$;
  isInSplitView$ = this.taskWorkspaceStore('splitViewOutputName').$.pipe(
    map(isDefined),
  );
  viewContainerMappingService = inject(ViewContainerMappingService);
  buildThumbnailContainerId =
    this.viewContainerMappingService.buildThumbnailContainerId;
  buildMainContainerId = this.viewContainerMappingService.buildMainContainerId;
  private sensorFusionContainer!: HTMLElement;
  private splitViewService = inject(SplitViewInitializationService);
  private featureFlagService = inject(FeatureFlagService);
  private eventTrackingService = inject(EventTrackingService);

  constructor(private taskWorkspaceStore: TaskWorkspaceStore) {
    super();
    this.observerCallback = this.buildObserverCallback();
    this.observer = new MutationObserver(this.observerCallback);
  }

  @ViewChild('sensorFusionContainer')
  set sensorFusionContainerElement(ref: ElementRef) {
    this.sensorFusionContainer = ref.nativeElement;
    setSharedFusionCssProperties(ref);
  }

  ngOnInit() {
    this.validOutputs = this.getFusedOutputs();
    this.taskWorkspaceStore('outputsByDisplayOrder').set(this.validOutputs);
    this.selectedIndex$ = this.taskWorkspaceStore('selectedOutputName').$.pipe(
      map((outputName) => this.getIndexFromOutputName(outputName)),
    );
    this.hasFeedback = this.getHasFeedback$();
  }

  ngAfterViewInit() {
    // Trigger initial tab selection and set focus on the selected output.
    this.setSelectedOutput(0);
    if (
      this.taskWorkspaceStore.isUsingObjects() &&
      getTotalFusedOutputs(this.taskWorkspaceStore.state().project) > 1
    ) {
      this.splitViewService.turnSplitViewOn();
    }

    this.observeDomMutations();
    this.overrideMaterialTabClickHandler();

    this.subscribeTo(
      combineLatest([
        this.taskWorkspaceStore('selectedOutputName').$,
        this.taskWorkspaceStore('splitViewOutputName').$,
      ]).pipe(filter(([_, splitViewOutputName]) => !splitViewOutputName)),
      ([selectedOutputName]) =>
        this.validOutputs.forEach((output) => {
          if (selectedOutputName === output.name) {
            this.viewContainerMappingService.switchOutputViewToMainCanvas(
              output.name,
            );
          } else {
            this.viewContainerMappingService.switchOutputViewToThumbnail(
              output.name,
            );
          }
        }),
    );
  }

  onTabChanged() {
    this.setFocusOnOutput();
  }

  handleKeyDownEvent($event: KeyboardEvent) {
    if (!this.featureFlagService.hasFlag('view_settings_shortcuts')) {
      return;
    }
    // when user focus on input in tag editor, we want to allow them resume
    // focus on output by pressing escape
    this.resumeFocusOnOutput($event);

    if (this.shouldIgnoreKeyboardEvent($event)) {
      return;
    }

    // Only react to keys 1 to 9
    if (+$event.key > 0) {
      this.setSelectedOutput(+$event.key - 1, 'keyboard_shortcut');
    }
  }

  private getHasFeedback$() {
    return mapToObject(this.validOutputs, (output) => [
      output.name,
      this.taskWorkspaceStore('shapeFeedback').$.pipe(
        map((shapeFeedback) =>
          shapeFeedback.some((feedback) => feedback.outputId === output.id),
        ),
      ),
    ]);
  }

  private overrideMaterialTabClickHandler() {
    // We want to disable the default tab switching behavior and switching
    // tabs based on selectedOutputId to avoid looping.
    this.tabGroup._handleClick = (
      _tab: MatTab,
      _tabHeader: MatTabGroupBaseHeader,
      index: number,
    ) => {
      this.setSelectedOutput(index, 'click_thumbnail');
      return false;
    };
  }

  private setSelectedOutput(index: number, source?: string) {
    const selectedOutput = this.validOutputs.at(index);
    if (selectedOutput) {
      this.taskWorkspaceStore('selectedOutputName').set(selectedOutput.name);
      source && this.trackActiveOutputChangedEvent(selectedOutput.name, source);
    }
  }

  private resumeFocusOnOutput($event: KeyboardEvent) {
    if ($event.key === 'Escape' && $event.target !== $event.currentTarget) {
      this.setFocusOnOutput();
    }
  }

  private shouldIgnoreKeyboardEvent($event: KeyboardEvent) {
    return (
      $event.target instanceof HTMLInputElement ||
      $event.target instanceof HTMLTextAreaElement
    );
  }

  private observeDomMutations() {
    this.observer.observe(this.sensorFusionContainer, {
      childList: true,
      subtree: true,
    });
  }

  private buildObserverCallback() {
    return () => {
      if (this.setFocusOnOutput()) {
        this.observer.disconnect();
      }
    };
  }

  private setFocusOnOutput() {
    const selectedIndex = this.getIndexFromOutputName(
      this.taskWorkspaceStore.state().selectedOutputName,
    );
    const output = this.validOutputs[selectedIndex];
    const el = getOutputHelper(output.type).getFocusableOutput(
      output.name,
      this.sensorFusionContainer,
    );

    if (el) {
      el.focus({ preventScroll: true });
    }
    return !!el;
  }

  private getFusedOutputs() {
    const { project } = this.taskWorkspaceStore.state();
    if (this.taskWorkspaceStore.isUsingObjects()) {
      return getFusedOutputs(project, this.displayOrder);
    }

    if (!this.fusionOutput) {
      return this.getValidOutputsLegacy();
    }

    return getFusedOutputsObjectless(
      project,
      this.fusionOutput,
      this.displayOrder,
    );
  }

  /**
   * @deprecated to be removed in upcoming releases
   */
  private getValidOutputsLegacy() {
    let legacyOutputNames;
    if (this.fusedOutputs.length) {
      legacyOutputNames = this.fusedOutputs
        .split(',')
        .map((item) => item.trim());
    } else {
      legacyOutputNames = this.outputs;
    }
    return legacyOutputNames.map((outputName) =>
      getOutputByName(this.taskWorkspaceStore.state().project, outputName),
    );
  }

  private getIndexFromOutputName(outputName: string | undefined) {
    return Math.max(
      0,
      this.validOutputs.findIndex((output) => output.name === outputName),
    );
  }

  private trackActiveOutputChangedEvent(
    activeOutputName: string,
    source: string,
  ) {
    this.eventTrackingService.track({
      id: 'change_active_output',
      bucket: 'navigation',
      active_output: activeOutputName,
      source,
    });
  }
}
